import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import moment from "moment";
import { Columns, Config, DefaultConfig, Pagination } from "ngx-easy-table";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { CandidateChecklistItem } from "src/app/models/models";
import { DateRange } from "src/app/shared/constants/angular-datetime-picker.const";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { defaultListDateRange, isWithinListDateFilter } from "src/app/shared/utils/list-date-filter.utils";
import { checklistItemCommands } from "./checklist-navigation";

@Component({
	selector: "app-checklist",
	templateUrl: "./checklist.component.html",
	styleUrls: ["./checklist.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ChecklistComponent implements OnInit {
	items: CandidateChecklistItem[] = [];
	private allItems: CandidateChecklistItem[] = [];
	private readonly initialRange = defaultListDateRange();
	startDate = this.initialRange.startDate;
	endDate = this.initialRange.endDate;
	dateFilterEnabled = false;

	public columns: Columns[] = [];
	public configuration!: Config;
	public pagination: Pagination = { limit: 10, offset: 1, count: 0 };

	@ViewChild("requestIdTpl", { static: true }) requestIdTpl!: TemplateRef<unknown>;
	@ViewChild("requestTypeTpl", { static: true }) requestTypeTpl!: TemplateRef<unknown>;
	@ViewChild("instructorTpl", { static: true }) instructorTpl!: TemplateRef<unknown>;
	@ViewChild("dateTpl", { static: true }) dateTpl!: TemplateRef<unknown>;
	@ViewChild("actionTpl", { static: true }) actionTpl!: TemplateRef<unknown>;

	constructor(
		private httpService: HttpService,
		private loaderService: LoaderService,
		public sharedService: SharedService,
		private roleService: RoleService,
		private router: Router,
	) {}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.CHECKLIST);
		this.initTableConfig();
		this.loadChecklist();
	}

	initTableConfig(): void {
		this.configuration = { ...DefaultConfig };
		this.configuration.searchEnabled = false;
		this.configuration.paginationEnabled = false;
		this.configuration.tableLayout.style = "bigger";

		this.columns = [
			{
				key: "requestId",
				title: "Request ID",
				cellTemplate: this.requestIdTpl,
			},
			{
				key: "requestType",
				title: "Request Type",
				cellTemplate: this.requestTypeTpl,
			},
			...(this.roleService.isInstructor
				? []
				: [
						{
							key: "instructorName",
							title: "From",
							cellTemplate: this.instructorTpl,
						},
					]),
			{
				key: "submittedAt",
				title: "Submission Date/Time",
				cellTemplate: this.dateTpl,
			},
			{
				key: "action",
				title: "Action",
				cellTemplate: this.actionTpl,
			},
		];
	}

	loadChecklist(): void {
		this.loaderService.setLoading(true);
		this.httpService.getCandidateChecklist().subscribe({
			next: (resp) => {
				const payload = resp?.data ?? resp ?? [];
				this.allItems = Array.isArray(payload) ? payload : [];
				this.applyDateFilter();
				this.loaderService.setLoading(false);
			},
			error: () => {
				this.items = [];
				this.loaderService.setLoading(false);
			},
		});
	}

	handleDateRangeChange(dateRange: DateRange): void {
		if (dateRange?.enabled === false) {
			this.dateFilterEnabled = false;
		} else if (dateRange?.startDate && dateRange?.endDate) {
			this.dateFilterEnabled = true;
			this.startDate = dateRange.startDate;
			this.endDate = dateRange.endDate;
		} else {
			return;
		}

		this.applyDateFilter();
	}

	private applyDateFilter(): void {
		this.items = this.allItems.filter((item) =>
			isWithinListDateFilter(item.submittedAt, this.dateFilterEnabled, this.startDate, this.endDate),
		);
		const count = Math.max(this.items.length, 1);
		this.pagination = { limit: count, offset: 1, count };
		this.configuration = { ...this.configuration, rows: count };
	}

	formatRequestId(trainingAssessmentId: string): string {
		const digits = trainingAssessmentId.replace(/\D/g, "");
		if (digits.length >= 7) {
			return `#${digits.slice(0, 7)}`;
		}
		return `#${trainingAssessmentId.replace(/-/g, "").slice(0, 7)}`;
	}

	fromLabel(item: CandidateChecklistItem): string {
		if (item.isStandalone || item.taskType === "standalone_feedback") {
			return "Training Department";
		}
		return item.instructorName || "—";
	}

	requestTypeLabel(item: CandidateChecklistItem): string {
		if (item.taskType === "standalone_feedback" || item.taskType === "feedback") {
			return "Feedback";
		}
		return "Sign Off";
	}

	formatRowRequestId(item: CandidateChecklistItem): string {
		if (item.taskType === "standalone_feedback" && item.taskId) {
			return this.formatRequestId(item.taskId);
		}
		if (item.trainingAssessmentId) {
			return this.formatRequestId(item.trainingAssessmentId);
		}
		return "—";
	}

	formatSubmittedAt(submittedAt?: string): string {
		if (submittedAt) {
			const parsed = moment(submittedAt);
			if (parsed.isValid()) {
				return parsed.format(CONSTANT.DATE_FORMAT.TABLE_DATE_FORMAT);
			}
		}

		return "—";
	}

	openItem(item: CandidateChecklistItem): void {
		const commands = checklistItemCommands(item);
		if (commands) {
			this.router.navigate(commands);
		}
	}
}
